import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Spinner from 'react-bootstrap/Spinner';

import {
    HiViewGrid, HiMenu, HiOutlineSearch,
    HiOutlineShoppingCart, HiOutlineUserCircle,
    HiOutlineLogout, HiOutlineLogin,
    HiOutlineHome, HiOutlineShoppingBag,
    HiOutlineHeart, HiOutlineQuestionMarkCircle,
    HiOutlineClipboardList, HiChevronDown
} from "react-icons/hi";


import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import Offcanvas from 'react-bootstrap/Offcanvas';
import NavDropdown from 'react-bootstrap/NavDropdown';
import { Link, NavLink, useNavigate } from 'react-router-dom';


import DarkLogo from "src/assets/images/dark_logo.png";
import { getTokenData, capitalize, isRTL } from 'src/utils/global';
import { clearAllStorage } from 'src/utils/auth';

import { authSelector, logOut } from 'src/store/reducers/auth';
import apiSlice from 'src/store/api/apiSlice';
import classes from './styles.module.css';
import logo from 'src/assets/images/logo.svg';
import CatsMenu from '../cats/CatsMenu';
import { cartCountSelector, cartLoadingSelector, cartReset } from 'src/store/reducers/cart';
import { useWishlistQuery } from 'src/store/api/accounts/accountsApiSlice';
import { useGuestWishlistState } from 'src/hooks';

const ICON_SIZE = 22
const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `${classes.item__link} nav-link ${isActive ? classes.active : ""}`;


export default () => {
    const { t } = useTranslation(['common']);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [openMenu, setOpenMenu] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [profileOpen, setProfileOpen] = useState(false);
    const auth = useSelector(authSelector);
    const userData = getTokenData(auth.access);
    const displayName = capitalize(userData?.username || '');
    const cartLoading = useSelector(cartLoadingSelector);
    const cartCount = useSelector(cartCountSelector);
    const cartCountLabel = cartCount > 99 ? "99+" : cartCount;
    const { slugs: guestWishlistSlugs, syncState: guestWishlistSyncState } = useGuestWishlistState();
    const isGuestWishlistMode = !auth.access || (guestWishlistSyncState === "syncing" && guestWishlistSlugs.length > 0);
    const { data: wishlistData } = useWishlistQuery(undefined, {
        refetchOnReconnect: true,
        refetchOnMountOrArgChange: true,
        skip: isGuestWishlistMode,
    });
    const wishlistCount = isGuestWishlistMode ? guestWishlistSlugs.length : (wishlistData?.count ?? 0);
    const wishlistCountLabel = wishlistCount > 99 ? "99+" : wishlistCount;

    // =============================
    // Handlers ====================
    // =============================
    const openMenuHandle = () => setOpenMenu(true);

    const searchSubmitHandle = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const term = searchTerm.trim();
        navigate(term ? `/products?search=${encodeURIComponent(term)}` : '/products?search=true');
    };

    const closeProfileHandle = () => setProfileOpen(false);

    const logoutHandle = () => {
        dispatch(logOut());
        dispatch(cartReset());
        dispatch(apiSlice.util.resetApiState());
        clearAllStorage();
    };


    return (
        <header>
            <Navbar key={'xl'} fixed="top" expand={'xl'} className={`${classes.nav} mb-3`}>
                <Container fluid>
                    <button
                        type="button"
                        className={classes.cat__toggle}
                        onClick={openMenuHandle}
                        aria-label={t("nav.categories.title")}
                    >
                        <HiViewGrid size={28} />
                    </button>
                    <Navbar.Brand href="/">
                        <img src={logo} alt='Malek Store' height={50} width={112} />
                    </Navbar.Brand>
                    <Navbar.Toggle className={classes.toggle} aria-controls={`offcanvasNavbar-expand-xl`} >
                        <HiMenu size={ICON_SIZE + 4} />
                    </Navbar.Toggle>
                    <Navbar.Offcanvas
                        id={`offcanvasNavbar-expand-xl`}
                        aria-labelledby={`offcanvasNavbarLabel-expand-xl`}
                        placement="end"
                    >
                        <Offcanvas.Header closeButton className={classes.offcanvas__header}>
                            <Offcanvas.Title id={`offcanvasNavbarLabel-expand-xl`}>
                                <img src={DarkLogo} alt={t("store_name")} />
                            </Offcanvas.Title>
                        </Offcanvas.Header>
                        <Offcanvas.Body className={classes.offcanvas__body}>
                            <Nav className="justify-content-start flex-grow-1 pe-3">
                                <NavLink className={navLinkClass} to="/">
                                    <HiOutlineHome />
                                    {t("nav.home")}
                                </NavLink>
                                <NavLink className={navLinkClass} to="/products">
                                    <HiOutlineShoppingBag />
                                    {t("nav.products")}
                                </NavLink>
                                <NavLink className={navLinkClass} to="about-us/">
                                    <HiOutlineQuestionMarkCircle />
                                    {t("nav.about_us")}
                                </NavLink>
                                <form className={classes.nav__search} role="search" onSubmit={searchSubmitHandle}>
                                    <input
                                        type="search"
                                        className={classes.nav__search__input}
                                        value={searchTerm}
                                        onChange={(event) => setSearchTerm(event.target.value)}
                                        placeholder={t("nav.search_placeholder")}
                                        aria-label={t("nav.search")}
                                    />
                                    <button type="submit" className={classes.nav__search__btn} aria-label={t("nav.search")}>
                                        <HiOutlineSearch size={18} />
                                    </button>
                                </form>
                                <div className={classes.icons__con}>
                                    <Link to={'/wishlist'} className={`wishlist ${classes.item__link}`} aria-label={t("nav.wishlist")}>
                                        <span className={classes.navIconWrap}>
                                            <HiOutlineHeart size={ICON_SIZE} />
                                            {wishlistCount > 0 ? (
                                                <span className={classes.navCountBadge}>{wishlistCountLabel}</span>
                                            ) : null}
                                        </span>
                                    </Link>
                                    <Link to={'/cart'} className={`cart ${classes.item__link}`} aria-label={t("nav.cart")}>
                                        <span className={classes.navIconWrap}>
                                            <HiOutlineShoppingCart size={ICON_SIZE} />
                                            {cartLoading || cartCount > 0 ? (
                                                <span className={classes.navCountBadge}>
                                                    {cartLoading ? <Spinner size="sm" /> : cartCountLabel}
                                                </span>
                                            ) : null}
                                        </span>
                                    </Link>

                                    {auth.access ?
                                        <NavDropdown
                                            className={classes.dropdown__menu}
                                            align="end"
                                            title={
                                                <span className={`${classes.item__link} ${classes.profile__trigger}`}>
                                                    <HiOutlineUserCircle size={ICON_SIZE} aria-hidden="true" />
                                                    <span className={classes.profile__name} title={displayName}>
                                                        {displayName}
                                                    </span>
                                                    {/* Bootstrap's own caret is hidden app-wide in global.css,
                                                        so the menu affordance is drawn explicitly here. */}
                                                    <HiChevronDown
                                                        className={`${classes.profile__caret} ${profileOpen ? classes.profile__caret__open : ""}`}
                                                        aria-hidden="true"
                                                    />
                                                </span>
                                            }
                                            id="basic-nav-dropdown"
                                            show={profileOpen}
                                            onToggle={setProfileOpen}
                                        >
                                            <Link
                                                className={`dropdown-item ${classes.dropdown__item}`}
                                                to={"/profile"}
                                                onClick={closeProfileHandle}
                                            >
                                                <HiOutlineUserCircle size={21} /> {t('common:nav.profile')}
                                            </Link>
                                            <Link
                                                className={`dropdown-item ${classes.dropdown__item}`}
                                                to={"/orders"}
                                                onClick={closeProfileHandle}
                                            >
                                                <HiOutlineClipboardList size={21} /> {t('common:nav.orders')}
                                            </Link>
                                            <NavDropdown.Item
                                                className={classes.dropdown__item}
                                                onClick={() => {
                                                    logoutHandle();
                                                    closeProfileHandle();
                                                }}
                                            >
                                                <HiOutlineLogout size={21} /> {t('common:logout')}
                                            </NavDropdown.Item>
                                        </NavDropdown>

                                        :
                                        <NavLink to={"/auth/login/"} className={classes.nav__btn} >
                                            <HiOutlineLogin size={20} style={!isRTL() ? { transform: 'scaleX(-1)' } : undefined} />
                                            {t("nav.signIn")}
                                        </NavLink>
                                    }
                                </div>
                            </Nav>
                        </Offcanvas.Body>
                    </Navbar.Offcanvas>
                </Container>
                <CatsMenu openMenu={openMenu} setOpenMenu={setOpenMenu} />
            </Navbar>
            <div className={classes.prop}>
            </div>
        </header >
    );
}
